import React, { useMemo, useEffect } from 'react'
import {
  ShowView,
  ShowController,
  TabbedShowLayout,
  TextField,
  Tab,
  Datagrid,
  ArrayField,
  RichTextField,
  FunctionField,
  BooleanField,
  useQuery,
  ListContextProvider,
  Pagination,
} from 'react-admin'
import keyBy from 'lodash/keyBy'
import {
  formatDate,
  formatGender,
  formatSportKind,
  formatWeightStatus,
  formatStatus,
  isStatusPending,
} from '../utils'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { Link } from '@material-ui/core'
import { usePagination } from '../../hooks'
import { RequestActionField } from '../../components/custom'
import { Brackets } from './Brackets'

const TournamentTitle = ({ record }) => {
  return <span>{record.name ? record.name : ''}</span>
}

const ShowTournament = (props) => {
  const currentTourId = useMemo(() => {
    return props.id
  }, [props])
  const tabPath = useMemo(() => {
    return props.location.pathname.split('/').pop()
  }, [props])
  useEffect(() => {
    setPage(1)
  }, [tabPath])
  const { page, perPage, setPage, setPerPage } = usePagination()
  const { data, total } = useQuery({
    type: 'getList',
    resource: 'tournament_categories/',
    payload: {
      pagination: { page, perPage },
      sort: { field: 'id', order: 'ASK' },
      filter: { tournament: currentTourId },
    },
  })

  return (
    <ShowController {...props}>
      {(controllerProps) => (
        <ShowView {...props} {...controllerProps} title={<TournamentTitle />}>
          <TabbedShowLayout>
            <Tab label='Турнир'>
              <TextField source='name' label='Название' />
              <FunctionField
                label='Вид спорта'
                render={(record) => formatSportKind(record.sportKind)}
              />
              <FunctionField
                label='Организатор турнира'
                render={(record) => (
                  <Link href={`/admin#/users/${record?.owner.id}/show`}>
                    <TextField
                      record={record}
                      source='owner.fullName'
                      label='Организатор'
                    />
                  </Link>
                )}
              />
              <TextField label='Место' source='place' emptyText='Не указано' />
              <TextField source='city.name' label='Город' />
              <TextField
                label='Адрес'
                source='address'
                emptyText='Не указано'
              />
              <TextField label='Сайт' source='site' emptyText='Не указано' />
              <TextField
                label='Максимальное кол-во регистраций'
                source='participantsLimit'
                emptyText='Нет'
              />
              <FunctionField
                label='Период проведения'
                render={(record) =>
                  `C ${formatDate(record.dateStart)} по ${formatDate(
                    record.dateEnd
                  )}`
                }
              />
              <FunctionField
                label='Ранняя регистрация'
                render={(record) =>
                  `C ${formatDate(
                    record.earlyRegisterDateStart
                  )} по ${formatDate(record.registerDateStart)}`
                }
              />
              <FunctionField
                label='Обычная регистрация'
                render={(record) =>
                  `C ${formatDate(record.registerDateStart)} по ${formatDate(
                    record.registerDateEnd
                  )}`
                }
              />
              <FunctionField
                label='Поздняя регистрация'
                render={(record) =>
                  `C ${formatDate(record.registerDateEnd)} по ${formatDate(
                    record.lateRegisterDateEnd
                  )}`
                }
              />
              <FunctionField
                label='Статус верификации'
                render={(record) => `${record.isStarted ? 'Да' : 'Нет'}`}
              />
            </Tab>
            {controllerProps.record?.description && (
              <Tab label='Описание'>
                <RichTextField
                  label='Описание'
                  source='description'
                  emptyText='Не указано'
                />
              </Tab>
            )}
            <Tab label='Участники' path='participants'>
              <ListContextProvider
                value={{
                  resource: 'tournament_categories',
                  data: keyBy(data, 'id'),
                  ids: data?.map((el) => el.id),
                  currentSort: { field: 'id', order: 'ASC' },
                  selectedIds: [],
                }}
              >
                <Datagrid>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls='panel1a-content'
                      id='panel1a-header'
                    >
                      <TextField source='name' label='' />
                    </AccordionSummary>
                    <AccordionDetails>
                      <ArrayField source='participants' fieldKey='id'>
                        <Datagrid rowClick={(id) => `/users/${id}/show`}>
                          <TextField source='user.fullName' label='ФИО' />
                          <TextField
                            source='user.email'
                            label='Email'
                            emptyText='Не указано'
                          />
                          <FunctionField
                            label='Пол'
                            render={(record) =>
                              formatGender(record.user.gender)
                            }
                          />
                          <TextField
                            source='club.name'
                            label='Клуб'
                            emptyText='Не указано'
                          />
                          <FunctionField
                            label='Статус взвешивания'
                            render={(record) =>
                              formatWeightStatus(record.fightWeighingStatus)
                            }
                          />
                        </Datagrid>
                      </ArrayField>
                    </AccordionDetails>
                  </Accordion>
                </Datagrid>
                <Pagination
                  page={page}
                  perPage={perPage}
                  setPage={setPage}
                  total={total}
                  setPerPage={setPerPage}
                />
              </ListContextProvider>
            </Tab>
            <Tab label='Заявки' path='requests'>
              <ListContextProvider
                value={{
                  resource: 'tournament_categories',
                  data: keyBy(data, 'id'),
                  ids: data?.map((el) => el.id),
                  currentSort: { field: 'id', order: 'ASC' },
                  selectedIds: [],
                }}
              >
                <Datagrid>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls='panel1a-content'
                      id='panel1a-header'
                    >
                      <TextField source='name' label='' />
                    </AccordionSummary>
                    <AccordionDetails>
                      <ArrayField source='participationRequests' fieldKey='id'>
                        <Datagrid>
                          <TextField source='user.fullName' label='ФИО' />
                          <TextField
                            source='user.email'
                            label='Email'
                            emptyText='Не указано'
                          />
                          <TextField
                            source='club.name'
                            label='Клуб'
                            emptyText='Не указано'
                          />
                          <FunctionField
                            label='Статус заявки'
                            render={(record) =>
                              isStatusPending(record.status) ? (
                                <RequestActionField
                                  label='Действия'
                                  participantId={record.id}
                                />
                              ) : (
                                formatStatus(record.status)
                              )
                            }
                          />
                        </Datagrid>
                      </ArrayField>
                    </AccordionDetails>
                  </Accordion>
                </Datagrid>
                <Pagination
                  page={page}
                  perPage={perPage}
                  setPage={setPage}
                  total={total}
                  setPerPage={setPerPage}
                />
              </ListContextProvider>
            </Tab>
            <Tab label='Сетки'>
              <Brackets {...props} />
            </Tab>
            <Tab label='Локации'>
              <ArrayField
                label='Группы локаций'
                source='locationGroups'
                fieldKey='id'
                empty='Не указано'
              >
                <Datagrid>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls='panel1a-content'
                      id='panel1a-header'
                    >
                      <TextField
                        source='name'
                        label=''
                        emptyText='Не указано'
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <ArrayField source='locations' fieldKey='id'>
                        <Datagrid>
                          <TextField
                            source='name'
                            label='Название'
                            emptyText='Не указано'
                          />
                          <TextField
                            source='type'
                            label='Тип'
                            emptyText='Не указано'
                          />

                          <TextField
                            source='groupIndex'
                            label='Индекс'
                            emptyText='Не указано'
                          />
                        </Datagrid>
                      </ArrayField>
                    </AccordionDetails>
                  </Accordion>
                </Datagrid>
              </ArrayField>
            </Tab>
            <Tab label='Категории'>
              <ArrayField
                label='Категории'
                source='categories'
                fieldKey='id'
                empty='Не указано'
              >
                <Datagrid>
                  <TextField source='id' label='Id' />
                  <TextField source='name' label='Название' />
                  <FunctionField
                    label='Возраст'
                    render={(record) =>
                      record.birthYear
                        ? `${record.birthYear} год рождения`
                        : `С ${record.minAge || 'Не указано'} по ${
                            record.maxAge || 'Не указано'
                          } лет`
                    }
                  />
                  <FunctionField
                    label='Вес'
                    render={(record) =>
                      `От ${record.weight.minWeight} до ${record.weight.maxWeight} кг`
                    }
                  />
                  <TextField
                    source='rank.sportsmanRank'
                    label='Ранг'
                    emptyText='Не указано'
                  />
                  <BooleanField
                    source='areFemalesAllowed'
                    label='Допуск девушек к участию'
                  />
                  <BooleanField
                    source='areMalesAllowed'
                    label='Допуск мужчин к участию'
                  />
                  <TextField
                    source='registerPrice'
                    label='Стоимость регистрации'
                  />
                  <TextField
                    source='earlyRegisterPrice'
                    label='Цена ранней регистрации'
                  />
                  <TextField
                    source='lateRegisterPrice'
                    label='Цена поздней регистрации'
                  />
                  <FunctionField
                    label='Количество участников'
                    render={(record) => record.participants.length}
                  />
                </Datagrid>
              </ArrayField>
            </Tab>
          </TabbedShowLayout>
        </ShowView>
      )}
    </ShowController>
  )
}

export default ShowTournament
